import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { FormattedNumberInput } from "@/components/ui/formatted-number-input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  MapPin, Calendar, Package, DollarSign, Building2, Shield, 
  AlertCircle, Layers, Box, Truck, CheckCircle, Send, X,
  ArrowRight, Weight, Ruler, Clock, UserPlus, Info, HelpCircle,
  XCircle, RefreshCw, Pencil
} from "lucide-react";
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest, getCsrfHeaders } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import type { Load, LoadItem, ExternalParty } from "@shared/schema";
import MemberLink from "./MemberLink";
import { UpgradeModal } from "@/components/UpgradeModal";
import { BILLING_OWNER_ONLY_MESSAGE } from "@shared/billingPermissions";
import { useAuth } from "@/hooks/useAuth";

function isMasked(name: string | null | undefined): boolean {
  return !!name && name.includes("●");
}

interface PublicUserInfo {
  id: string;
  company: string;
  companyLogo?: string | null;
  dotNumber?: string;
  mcNumber?: string;
  adminVerified: boolean;
  upvoteCount: number;
  completedShipments: number;
  userType: string;
}

interface LoadWithPoster extends Load {
  poster: PublicUserInfo | null;
  items?: LoadItem[];
}

interface LoadDetailModalProps {
  load: LoadWithPoster | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentUserId?: string;
  currentUserType?: string;
  currentUserVerified?: boolean;
  readOnly?: boolean;
  onLoadStatusChange?: () => void;
  onEditLoad?: (load: LoadWithPoster) => void;
}

function FreightItemDetail({ item, index }: { item: LoadItem; index: number }) {
  const getItemIcon = (unit: string) => {
    switch (unit) {
      case "Pallet": return <Layers className="w-4 h-4" />;
      case "Crate": return <Box className="w-4 h-4" />;
      default: return <Package className="w-4 h-4" />;
    }
  };

  return (
    <div className="p-3 bg-muted/50 rounded-lg" data-testid={`freight-item-detail-${index}`}>
      <div className="flex items-center gap-2 mb-2">
        {getItemIcon(item.handlingUnit || "Other")}
        <span className="font-medium">{item.handlingUnit || "Item"}</span>
        <Badge variant="secondary" className="text-xs">Qty: {item.quantity || 1}</Badge>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
        {(item.length || item.width || item.height) && (
          <div className="flex items-center gap-1">
            <Ruler className="w-3 h-3" />
            <span>{item.length || 0}" × {item.width || 0}" × {item.height || 0}"</span>
          </div>
        )}
        <div className="flex items-center gap-1">
          <Weight className="w-3 h-3" />
          <span>{((item.weight || 0) * (item.quantity || 1)).toLocaleString()} lbs</span>
        </div>
      </div>
    </div>
  );
}

export default function LoadDetailModal({ 
  load, 
  open, 
  onOpenChange, 
  currentUserId,
  currentUserType,
  currentUserVerified = false,
  readOnly = false,
  onLoadStatusChange,
  onEditLoad
}: LoadDetailModalProps) {
  const { user, isTeamMember } = useAuth();
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [showExternalCarrierForm, setShowExternalCarrierForm] = useState(false);
  const [selectedExternalCarrierId, setSelectedExternalCarrierId] = useState("");
  const [quotePrice, setQuotePrice] = useState("");
  const [quoteTransitTime, setQuoteTransitTime] = useState("");
  const [quotePickupDate, setQuotePickupDate] = useState("");
  const [quoteMessage, setQuoteMessage] = useState("");
  const [questionText, setQuestionText] = useState("");
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState<"FREE_TIER_INVITED_ONLY" | "BROKER_UPGRADE_REQUIRED" | "FREE_TIER_LIMIT_REACHED" | "SEARCH_AND_BID_REQUIRED">("FREE_TIER_INVITED_ONLY");
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const isBrokerOwnLoad = currentUserType === "Broker" && load?.userId === currentUserId;
  const canAssignExternalCarrier = isBrokerOwnLoad && load?.status === "Available" && !load?.claimedByUserId;

  const closeLoadMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("PATCH", `/api/loads/${load?.id}/close`);
    },
    onSuccess: () => {
      toast({ title: "Load Closed", description: "This load is no longer visible to carriers." });
      queryClient.invalidateQueries({ queryKey: ['/api/loads'] });
      onLoadStatusChange?.();
      onOpenChange(false);
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to close load.", variant: "destructive" });
    }
  });

  const reactivateLoadMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("PATCH", `/api/loads/${load?.id}/reactivate`);
    },
    onSuccess: () => {
      toast({ title: "Load Reactivated", description: "This load is now visible to carriers again." });
      queryClient.invalidateQueries({ queryKey: ['/api/loads'] });
      onLoadStatusChange?.();
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message.startsWith("403: Your subscription") ? error.message : "Failed to reactivate load.", variant: "destructive" });
    }
  });

  const { data: externalCarriers = [] } = useQuery<ExternalParty[]>({
    queryKey: ['/api/external-parties', 'carriers'],
    queryFn: async () => {
      const response = await fetch('/api/external-parties');
      if (!response.ok) throw new Error('Failed to fetch external parties');
      const parties = await response.json();
      return parties.filter((p: ExternalParty) => p.partyType === 'carrier');
    },
    enabled: canAssignExternalCarrier,
  });

  const canPotentiallySubmitQuote =
    !readOnly &&
    (currentUserType === "Carrier" || currentUserType === "Broker") &&
    !!load?.id &&
    load?.userId !== currentUserId &&
    !(currentUserType === "Broker" && load?.poster?.userType === "Broker");

  const { data: threadStatusData } = useQuery<{ threadPhase: string | null }>({
    queryKey: ['/api/messages/thread-status', load?.id],
    enabled: open && canPotentiallySubmitQuote,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const assignExternalCarrierMutation = useMutation({
    mutationFn: async (data: { externalCarrierId: string; sendInvitation: boolean }) => {
      const response = await apiRequest('POST', `/api/loads/${load?.id}/assign-external`, data);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Carrier Assigned",
        description: "The external carrier has been assigned to this shipment. The deal is now active.",
      });
      queryClient.invalidateQueries({ queryKey: ['/api/loads'] });
      setShowExternalCarrierForm(false);
      setSelectedExternalCarrierId("");
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const submitQuoteMutation = useMutation({
    mutationFn: async (data: { toUserId: string; loadId: string; price: string; message: string; quoteTransitTime: string; quotePickupDate: string | undefined }) => {
      const response = await fetch("/api/messages/quote-submission", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(["Cancelled", "Suspended"].includes(errorData.membershipTier) ? errorData.message : errorData.error || "Failed to submit quote");
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Quote Submitted",
        description: "Your quote has been sent to the shipper.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/messages"] });
      queryClient.invalidateQueries({ queryKey: ['/api/messages/thread-status', load?.id] });
      setShowQuoteForm(false);
      setQuotePrice("");
      setQuoteTransitTime("");
      setQuotePickupDate("");
      setQuoteMessage("");
      onOpenChange(false);
      setLocation("/shipments");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const askQuestionMutation = useMutation({
    mutationFn: async (data: { toUserId: string; loadId: string; question: string }) => {
      const response = await fetch("/api/messages/ask-question", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(["Cancelled", "Suspended"].includes(errorData.membershipTier) ? errorData.message : errorData.error || "Failed to send question");
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Question Sent",
        description: "Your question has been sent to the shipper. You can continue the conversation in Messages.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/messages"] });
      setShowQuestionForm(false);
      setQuestionText("");
      onOpenChange(false);
      setLocation("/shipments");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  if (!load) return null;

  const isOwnLoad = load.userId === currentUserId;
  const isDoubleBrokerScenario = currentUserType === "Broker" && load.poster?.userType === "Broker" && !isOwnLoad;
  const canSubmitQuote = !isOwnLoad && !isDoubleBrokerScenario && (currentUserType === "Carrier" || currentUserType === "Broker");
  const activeQuotePhases = ['quote_submitted', 'active_shipment'];
  const hasActiveQuote = activeQuotePhases.includes(threadStatusData?.threadPhase ?? '');
  
  const totalWeight = load.items?.reduce((sum, item) => 
    sum + ((item.weight || 0) * (item.quantity || 1)), 0) || load.weight || 0;
  const totalItems = load.items?.reduce((sum, item) => sum + (item.quantity || 1), 0) || 1;
  
  const hasStackableItems = load.items?.some(item => item.stackable) || false;
  const hasHazardousItems = load.items?.some(item => item.hazardous) || false;

  const generateQuoteTemplate = () => {
    const pickupDate = load.pickupDate ? new Date(load.pickupDate).toLocaleDateString() : "TBD";
    const deliveryDate = load.deliveryDate ? new Date(load.deliveryDate).toLocaleDateString() : null;
    const loadRef = load.loadIdentifier ? `Load ${load.loadIdentifier}` : 'this load';
    
    return `Hi,

I'm interested in hauling ${loadRef} and would like to submit a quote.

Load Summary:
${load.loadIdentifier ? `• Load ID: ${load.loadIdentifier}\n` : ''}• Route: ${load.origin} → ${load.destination}
• Weight: ${totalWeight.toLocaleString()} lbs (${totalItems} item${totalItems > 1 ? 's' : ''})
• Pickup: ${pickupDate}
${deliveryDate ? `• Delivery: ${deliveryDate}\n` : ""}

My Quote: $${quotePrice || "[Enter your price]"}

I can meet your pickup and delivery requirements. Please let me know if you'd like to proceed or if you have any questions.

Thank you!`;
  };

  const handleSubmitQuote = () => {
    if (!quotePrice || !load.userId) {
      toast({
        title: "Error",
        description: "Please enter a quote price",
        variant: "destructive",
      });
      return;
    }

    const message = quoteMessage || generateQuoteTemplate();
    
    submitQuoteMutation.mutate({
      toUserId: load.userId,
      loadId: load.id,
      price: quotePrice,
      message,
      quoteTransitTime,
      quotePickupDate: quotePickupDate ? new Date(quotePickupDate).toISOString() : undefined,
    });
  };

  async function handleBidGateCheck(action: "quote" | "question") {
    if (!load) return;
    const tier = user?.membershipTier ?? "Free";
    if (tier === "Paid" || tier === "Annual" || tier === "Admin") {
      if (action === "quote") handleStartQuote();
      else setShowQuestionForm(true);
      return;
    }
    try {
      const res = await fetch(`/api/loads/${load.id}/can-bid`);
      const data = await res.json();
      if (data.allowed) {
        if (action === "quote") handleStartQuote();
        else setShowQuestionForm(true);
      } else {
        setUpgradeReason(data.reason ?? "FREE_TIER_INVITED_ONLY");
        setUpgradeModalOpen(true);
      }
    } catch {
      if (action === "quote") handleStartQuote();
      else setShowQuestionForm(true);
    }
  }

  const handleStartQuote = () => {
    setQuoteMessage(generateQuoteTemplate());
    setShowQuoteForm(true);
  };

  const handleAskQuestion = () => {
    if (!questionText.trim() || !load.userId) {
      toast({
        title: "Error",
        description: "Please enter your question",
        variant: "destructive",
      });
      return;
    }

    askQuestionMutation.mutate({
      toUserId: load.userId,
      loadId: load.id,
      question: questionText.trim(),
    });
  };

  const showFooter =
    !readOnly &&
    canSubmitQuote &&
    !isDoubleBrokerScenario &&
    currentUserVerified &&
    load.status === "Available" &&
    !showQuoteForm &&
    !showQuestionForm &&
    !showExternalCarrierForm;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden p-0 gap-0">

        {/* Fixed header */}
        <div className="px-6 pt-6 pb-4 shrink-0">
          <DialogHeader>
            <div className="flex items-center justify-between gap-2">
              <DialogTitle className="flex items-center gap-2" data-testid="modal-title">
                <Truck className="w-5 h-5" />
                Load Details
              </DialogTitle>
              {load.loadIdentifier && (
                <Badge variant="outline" className="font-mono text-sm" data-testid="text-load-identifier">
                  {load.loadIdentifier}
                </Badge>
              )}
            </div>
            <DialogDescription>
              {load.origin} → {load.destination}
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-6">

            {/* Shipper Info */}
            {load.poster && (
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <div className="flex-1">
                  {isMasked(load.poster.company) ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <span
                          className="font-medium select-none"
                          style={{ filter: "blur(4px)", userSelect: "none", pointerEvents: "none" }}
                          data-testid="text-shipper-masked"
                        >
                          {load.poster.company}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Upgrade your plan to see company details and bid on this load.
                      </p>
                      {isTeamMember ? (
                        <p className="text-sm text-muted-foreground">{BILLING_OWNER_ONLY_MESSAGE}</p>
                      ) : user?.userType === "Broker" ? (
                        <Button
                          size="sm"
                          onClick={() => setLocation("/upgrade")}
                        >
                          Upgrade to bid
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={async () => {
                            const membershipTier = user?.userType === "Carrier" ? "SearchAndBid" : "Paid";
                            const returnTo = `/find-loads?highlight=${load.id}`;
                            const res = await fetch("/api/create-checkout-session", {
                              method: "POST",
                              credentials: "include",
                              headers: { "Content-Type": "application/json", ...getCsrfHeaders("POST") },
                              body: JSON.stringify({ billingCycle: "monthly", membershipTier, returnTo }),
                            });
                            const data = await res.json();
                            if (data.url) window.location.href = data.url;
                          }}
                        >
                          Upgrade to bid
                        </Button>
                      )}
                    </div>
                  ) : (
                    <MemberLink
                      userId={load.poster.id}
                      name={load.poster.company}
                      companyLogo={load.poster.companyLogo}
                      showAvatar
                      avatarSize="md"
                      className="font-medium"
                      data-testid="link-shipper-company"
                    />
                  )}
                  {!isMasked(load.poster.company) && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Badge variant="secondary" className="text-xs">{load.poster.userType}</Badge>
                      {load.poster.adminVerified && (
                        <Badge variant="outline" className="text-green-600 border-green-600 text-xs">
                          <Shield className="w-3 h-3 mr-1" />
                          Verified
                        </Badge>
                      )}
                      {load.poster.completedShipments > 0 && (
                        <span>{load.poster.completedShipments} shipments</span>
                      )}
                      {load.poster.upvoteCount > 0 && (
                        <span>+{load.poster.upvoteCount} upvotes</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Route */}
            <div className="space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Route
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-3 border rounded-lg">
                  <div className="flex items-center gap-2 text-green-600 mb-1">
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm font-medium">Pickup</span>
                  </div>
                  <p className="font-medium" data-testid="text-load-origin">{load.origin}</p>
                  {load.pickupLocationType && load.pickupLocationType !== "Business" && (
                    <Badge variant="secondary" className="mt-1 text-xs">{load.pickupLocationType}</Badge>
                  )}
                </div>
                <div className="p-3 border rounded-lg">
                  <div className="flex items-center gap-2 text-red-600 mb-1">
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm font-medium">Delivery</span>
                  </div>
                  <p className="font-medium" data-testid="text-load-destination">{load.destination}</p>
                  {load.deliveryLocationType && load.deliveryLocationType !== "Business" && (
                    <Badge variant="secondary" className="mt-1 text-xs">{load.deliveryLocationType}</Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Schedule */}
            <div className="space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Schedule
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Pickup Date</p>
                    <p className="font-medium" data-testid="text-pickup-date">
                      {load.pickupDate ? new Date(load.pickupDate).toLocaleDateString() : "Flexible"}
                    </p>
                  </div>
                </div>
                {load.deliveryDate && (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Delivery Date</p>
                      <p className="font-medium" data-testid="text-delivery-date">
                        {new Date(load.deliveryDate).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Freight Items */}
            <div className="space-y-3">
              <h3 className="font-semibold flex items-center gap-2">
                <Package className="w-4 h-4" />
                Freight ({totalItems} item{totalItems > 1 ? 's' : ''}, {totalWeight.toLocaleString()} lbs total)
              </h3>
              <div className="space-y-2">
                {load.items && load.items.length > 0 ? (
                  load.items.map((item, idx) => (
                    <FreightItemDetail key={idx} item={item} index={idx} />
                  ))
                ) : (
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      <span className="font-medium">{load.handlingUnit || "Standard Freight"}</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {load.weight?.toLocaleString()} lbs
                      {load.dimensions && ` • ${load.dimensions}`}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Special Information */}
            {(load.liftgatePickup || load.insidePickup || load.liftgateDelivery || 
              load.insideDelivery || load.deliveryAppointmentRequired || load.callBeforeDelivery ||
              hasStackableItems || hasHazardousItems) && (
              <div className="space-y-3">
                <h3 className="font-semibold">Special Information</h3>
                <div className="flex flex-wrap gap-2">
                  {hasHazardousItems && (
                    <Badge variant="outline" className="text-yellow-600 border-yellow-600">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Contains Hazardous Materials
                    </Badge>
                  )}
                  {hasStackableItems && (
                    <Badge variant="outline" className="text-green-600 border-green-600">
                      Freight is Stackable
                    </Badge>
                  )}
                  {load.liftgatePickup && <Badge variant="secondary">Liftgate at Pickup</Badge>}
                  {load.insidePickup && <Badge variant="secondary">Inside Pickup</Badge>}
                  {load.liftgateDelivery && <Badge variant="secondary">Liftgate at Delivery</Badge>}
                  {load.insideDelivery && <Badge variant="secondary">Inside Delivery</Badge>}
                  {load.deliveryAppointmentRequired && <Badge variant="secondary">Appointment Required</Badge>}
                  {load.callBeforeDelivery && <Badge variant="secondary">Call Before Delivery</Badge>}
                </div>
              </div>
            )}

            {/* Shipper Notes */}
            {load.details && (currentUserVerified || isOwnLoad) && (
              <div className="space-y-2">
                <h3 className="font-semibold">Shipper Notes</h3>
                <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg" data-testid="text-load-details">
                  {load.details}
                </p>
              </div>
            )}

            {/* External Carrier Assignment Form */}
            {showExternalCarrierForm && (
              <div className="space-y-4 p-4 border rounded-lg">
                <h3 className="font-semibold flex items-center gap-2">
                  <UserPlus className="w-4 h-4" />
                  Assign External Carrier
                </h3>

                <div className="p-3 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 shrink-0" />
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      The shipment will be confirmed immediately once you assign a carrier. 
                      An invitation to join Fill The Trucks will be sent to the carrier, 
                      but this is optional and doesn't affect the deal.
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="external-carrier">Select Carrier</Label>
                  <Select 
                    value={selectedExternalCarrierId} 
                    onValueChange={setSelectedExternalCarrierId}
                  >
                    <SelectTrigger data-testid="select-external-carrier">
                      <SelectValue placeholder="Choose a carrier from your contacts" />
                    </SelectTrigger>
                    <SelectContent>
                      {externalCarriers.map((carrier) => (
                        <SelectItem 
                          key={carrier.id} 
                          value={carrier.id}
                          data-testid={`option-carrier-${carrier.id}`}
                        >
                          {carrier.companyName} {carrier.dotNumber && `(DOT: ${carrier.dotNumber})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-3">
                  <Button
                    className="flex-1"
                    onClick={() => {
                      if (selectedExternalCarrierId) {
                        assignExternalCarrierMutation.mutate({
                          externalCarrierId: selectedExternalCarrierId,
                          sendInvitation: true,
                        });
                      }
                    }}
                    disabled={!selectedExternalCarrierId || assignExternalCarrierMutation.isPending}
                    data-testid="button-assign-external-carrier"
                  >
                    {assignExternalCarrierMutation.isPending ? (
                      "Assigning..."
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Assign & Send Invitation
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowExternalCarrierForm(false);
                      setSelectedExternalCarrierId("");
                    }}
                    data-testid="button-cancel-external-assignment"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Owner Actions - Close/Reactivate/Edit */}
            {isOwnLoad && !load.claimedByUserId && load.status !== "Completed" && load.status !== "Confirmed" && (
              <div className="flex gap-3 flex-wrap">
                {onEditLoad && (
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      onEditLoad(load);
                      onOpenChange(false);
                    }}
                    data-testid="button-edit-load-detail"
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit Load
                  </Button>
                )}
                {load.status === "Closed" && (
                  <Button 
                    className="flex-1"
                    onClick={() => reactivateLoadMutation.mutate()}
                    disabled={reactivateLoadMutation.isPending}
                    data-testid="button-reactivate-load-detail"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${reactivateLoadMutation.isPending ? 'animate-spin' : ''}`} />
                    {reactivateLoadMutation.isPending ? "Reactivating..." : "Reactivate Load"}
                  </Button>
                )}
              </div>
            )}

            {/* Double-broker protection alert */}
            {!readOnly && isDoubleBrokerScenario && (
              <div className="p-3 bg-muted/60 border rounded-lg" data-testid="alert-double-broker">
                <div className="flex items-start gap-2 text-muted-foreground">
                  <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <p className="text-sm">This load is posted by a broker. To prevent double-brokering, only carriers can quote on or claim broker-posted loads.</p>
                </div>
              </div>
            )}


            {/* Assign External Carrier trigger */}
            {canAssignExternalCarrier && !showExternalCarrierForm && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setShowExternalCarrierForm(true)}
                data-testid="button-assign-external-carrier-trigger"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Assign External Carrier
              </Button>
            )}

            {/* Quote form (scrolls within content area) */}
            {showQuoteForm && (
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Submit Your Quote
                </h3>
                
                <div className="space-y-2">
                  <Label htmlFor="quote-price">Your Quote Price ($) *</Label>
                  <FormattedNumberInput
                    id="quote-price"
                    isCurrency
                    decimals={2}
                    placeholder="e.g. 5,000.00"
                    value={quotePrice}
                    onChange={(val) => {
                      setQuotePrice(val);
                      if (val) {
                        const formatted = Number(val).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                        setQuoteMessage(prev => prev.replace(/\$\[Enter your price\]|\$[\d,]+\.?\d*/g, `$${formatted}`));
                      }
                    }}
                    data-testid="input-quote-price"
                  />
                </div>

                {/* Transit time and pickup date — side by side */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="quote-transit-time">Estimated transit time <span className="text-destructive">*</span></Label>
                    <Select value={quoteTransitTime} onValueChange={setQuoteTransitTime}>
                      <SelectTrigger id="quote-transit-time" data-testid="select-quote-transit-time">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1 day">1 day</SelectItem>
                        <SelectItem value="2 days">2 days</SelectItem>
                        <SelectItem value="3 days">3 days</SelectItem>
                        <SelectItem value="4-5 days">4–5 days</SelectItem>
                        <SelectItem value="5+ days">5+ days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="quote-pickup-date">Available pickup date <span className="text-destructive">*</span></Label>
                    <Input
                      id="quote-pickup-date"
                      type="date"
                      value={quotePickupDate}
                      onChange={(e) => setQuotePickupDate(e.target.value)}
                      data-testid="input-quote-pickup-date"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="quote-message">Message to Shipper</Label>
                  <Textarea
                    id="quote-message"
                    rows={10}
                    value={quoteMessage}
                    onChange={(e) => setQuoteMessage(e.target.value)}
                    data-testid="textarea-quote-message"
                  />
                  <p className="text-xs text-muted-foreground">
                    Edit the message above to customize your quote
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button 
                    className="flex-1"
                    onClick={handleSubmitQuote}
                    disabled={!quotePrice || !quoteTransitTime || !quotePickupDate || submitQuoteMutation.isPending}
                    data-testid="button-send-quote"
                  >
                    {submitQuoteMutation.isPending ? (
                      "Sending..."
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Quote
                      </>
                    )}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setShowQuoteForm(false);
                      setQuoteTransitTime("");
                      setQuotePickupDate("");
                    }}
                    data-testid="button-cancel-quote"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Question form (scrolls within content area) */}
            {showQuestionForm && (
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <HelpCircle className="w-4 h-4" />
                  Ask a Question
                </h3>
                
                <div className="p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
                  <p>Have questions before submitting a quote? Ask here. This conversation must result in a Quote or will be closed.</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="question-text">Your Question *</Label>
                  <Textarea
                    id="question-text"
                    rows={4}
                    placeholder="e.g., What time is the pickup appointment? Is there a dock at the delivery location?"
                    value={questionText}
                    onChange={(e) => setQuestionText(e.target.value)}
                    data-testid="textarea-question"
                  />
                </div>

                <div className="flex gap-3">
                  <Button 
                    className="flex-1"
                    onClick={handleAskQuestion}
                    disabled={!questionText.trim() || askQuestionMutation.isPending}
                    data-testid="button-send-question"
                  >
                    {askQuestionMutation.isPending ? (
                      "Sending..."
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Question
                      </>
                    )}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      setShowQuestionForm(false);
                      setQuestionText("");
                    }}
                    data-testid="button-cancel-question"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Fixed footer — Submit Quote and Ask a Question always visible */}
        {showFooter && (
          <div className="border-t border-border px-6 py-4 bg-background flex flex-col gap-2 shrink-0">
            {hasActiveQuote ? (
              <div className="flex items-center gap-2" data-testid="badge-quote-submitted">
                <CheckCircle className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Quote submitted</span>
              </div>
            ) : (
              <Button
                className="w-full"
                onClick={() => handleBidGateCheck("quote")}
                data-testid="button-submit-quote"
              >
                <Send className="w-4 h-4 mr-2" />
                Submit Quote
              </Button>
            )}
            <Button
              variant="outline"
              className="w-full"
              onClick={() => handleBidGateCheck("question")}
              data-testid="button-ask-question"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Ask a Question
            </Button>
          </div>
        )}

      </DialogContent>
    </Dialog>

    <UpgradeModal
      open={upgradeModalOpen}
      onClose={() => setUpgradeModalOpen(false)}
      reason={upgradeReason}
      userType={user?.userType ?? undefined}
    />
    </>
  );
}
